import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Input,
  Button,
  Empty,
  Dropdown,
  Menu,
  message as antMessage,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  MoreOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import ConversationItem from './ConversationItem';
import styles from '../../styles/chatV2.module.css';

const ConversationSidebar = ({
  visible,
  onClose,
  conversations = [],
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onRenameConversation,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = conversations.filter((conv) =>
        conv.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredConversations(filtered);
    } else {
      setFilteredConversations(conversations);
    }
  }, [searchTerm, conversations]);

  const handleDelete = (conversationId) => {
    if (onDeleteConversation) {
      onDeleteConversation(conversationId);
      antMessage.success('Conversation deleted');
    }
  };

  const handleRename = (conversationId, newTitle) => {
    if (onRenameConversation) {
      onRenameConversation(conversationId, newTitle);
      antMessage.success('Conversation renamed');
    }
  };

  const groupedConversations = {
    today: [],
    yesterday: [],
    lastWeek: [],
    older: [],
  };

  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(now);
  lastWeek.setDate(lastWeek.getDate() - 7);

  filteredConversations.forEach((conv) => {
    const convDate = new Date(conv.updatedAt || conv.createdAt);

    if (convDate.toDateString() === now.toDateString()) {
      groupedConversations.today.push(conv);
    } else if (convDate.toDateString() === yesterday.toDateString()) {
      groupedConversations.yesterday.push(conv);
    } else if (convDate > lastWeek) {
      groupedConversations.lastWeek.push(conv);
    } else {
      groupedConversations.older.push(conv);
    }
  });

  const renderGroup = (title, conversations) => {
    if (conversations.length === 0) return null;

    return (
      <div className={styles.conversationGroup}>
        <div className={styles.groupTitle}>{title}</div>
        {conversations.map((conv) => (
          <ConversationItem
            key={conv.conversationId}
            conversation={conv}
            isActive={conv.conversationId === activeConversationId}
            onSelect={() => onSelectConversation(conv)}
            onDelete={() => handleDelete(conv.conversationId)}
            onRename={(newTitle) =>
              handleRename(conv.conversationId, newTitle)
            }
          />
        ))}
      </div>
    );
  };

  return (
    <Drawer
      title="Conversations"
      placement="left"
      onClose={onClose}
      visible={visible}
      width={320}
      className={styles.conversationDrawer}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onNewConversation}
        >
          New Chat
        </Button>
      }
    >
      {/* Search Bar */}
      <div className={styles.conversationSearch}>
        <Input
          placeholder="Search conversations..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />
      </div>

      {/* Conversation List */}
      <div className={styles.conversationList}>
        {filteredConversations.length === 0 ? (
          <Empty
            description="No conversations yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <>
            {renderGroup('Today', groupedConversations.today)}
            {renderGroup('Yesterday', groupedConversations.yesterday)}
            {renderGroup('Last 7 Days', groupedConversations.lastWeek)}
            {renderGroup('Older', groupedConversations.older)}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default ConversationSidebar;
